// src/helpers/useUITranslations.js
import { ref, onMounted, watch } from 'vue'
import db from '@/firebase/init'
import { useLanguages } from './useLanguages'

export function useUITranslations() {
  const { enabledLangs, defaultLang } = useLanguages()
  const translations = ref({})
  const loading = ref(false)
  const error = ref(null)

  const loadTranslations = async (langCode) => {
    if (translations.value[langCode]) {
      return translations.value[langCode]
    }

    loading.value = true
    error.value = null

    try {
      const doc = await db.collection('uiTranslations').doc(langCode).get()

      if (doc.exists) {
        const data = doc.data()
        // New structure: data.translations contains the full JSON object
        const translationData = data.translations || data
        translations.value[langCode] = translationData
        return translationData
      } else {
        // Fallback to default language
        const defaultDoc = await db
          .collection('uiTranslations')
          .doc(defaultLang.value)
          .get()
        if (defaultDoc.exists) {
          const defaultData = defaultDoc.data()
          const translationData = defaultData.translations || defaultData
          translations.value[langCode] = translationData
          return translationData
        }
        throw new Error(
          `No translations found for ${langCode} or ${defaultLang.value}`
        )
      }
    } catch (err) {
      // Silently handle permissions errors - fallback to hardcoded content
      if (err.code === 'permission-denied') {
        console.warn(
          'UI translations not accessible due to permissions. Using fallback content.'
        )
      } else {
        console.error('Error loading UI translations:', err)
      }
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  const t = (key, langCode = null) => {
    const lang = langCode || defaultLang.value
    const translation = translations.value[lang]

    if (!translation) {
      return key
    }

    // Navigate through nested object using dot notation
    const keys = key.split('.')
    let result = translation

    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k]
      } else {
        return key
      }
    }

    return result || key
  }

  const loadAllTranslations = async () => {
    if (!enabledLangs.value || enabledLangs.value.length === 0) {
      return
    }

    const promises = enabledLangs.value.map((lang) =>
      loadTranslations(lang.code)
    )
    await Promise.all(promises)
  }

  onMounted(async () => {
    await loadAllTranslations()

    // Listen for translation updates from admin interface
    window.addEventListener('translationsUpdated', async (event) => {
      console.log('Translations updated, reloading...', event.detail)
      await loadAllTranslations()
    })
  })

  // Watch for changes in enabledLangs and reload translations
  watch(
    enabledLangs,
    async (newLangs) => {
      if (newLangs && newLangs.length > 0) {
        await loadAllTranslations()
      }
    },
    { immediate: true }
  )

  return {
    translations,
    loading,
    error,
    t,
    loadTranslations,
    loadAllTranslations,
  }
}
